import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/react-hooks';
import {
  Alert,
  AlertIcon,
  Box,
  CloseButton,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';

import useHeaderComponentsMutation from '../../../Hooks/useHeaderComponentsMutation';
import { EVENT_TITLE } from '../../../Constants/AppConstants';
import { AddTemplateForm } from './AddTemplateForm';
import { TemplateRow } from './TemplateRow';
import { SPIRIT_POINT_TEMPLATE } from '../../../GraphQL/Types';
import { Provider } from '../../../Chakra/provider';

const FETCH_SPIRIT_POINT_TEMPLATES = gql`
  query fetchEventDetail($id: Int!) {
    fetchEventDetail(id: $id) {
      id
      title
      spiritPointTemplates {
        ${SPIRIT_POINT_TEMPLATE}
      }
    }
  }
`;

export const SpiritPointTemplates = () => {
  const { id } = useParams();
  const eventId = parseInt(id, 10);
  const [errorMessage, setErrorMessage] = useState('');

  const { data, loading, refetch } = useQuery(FETCH_SPIRIT_POINT_TEMPLATES, {
    variables: { id: eventId },
    fetchPolicy: 'no-cache',
  });

  const existingTemplates = data?.fetchEventDetail?.spiritPointTemplates || [];
  const templates = [...existingTemplates].sort((a, b) => b.amount - a.amount);
  const eventTitle = data?.fetchEventDetail?.title || '';

  useHeaderComponentsMutation({
    title: 'SPIRIT POINT TEMPLATES',
    backLink: `/event-manage/${id}`,
    components: [{ key: EVENT_TITLE, value: eventTitle }],
  });

  if (loading) {
    return (
      <Provider>
        <Flex w="100%" h="100%" justifyContent="center" alignItems="center">
          <Spinner size="xl" />
        </Flex>
      </Provider>
    );
  }

  const onTemplateCreate = () => {
    refetch();
  };

  const onTemplateUpdate = () => {
    refetch();
  };

  const onTemplateRemoved = () => {
    refetch();
  };

  const setError = (msg) => {
    setErrorMessage(msg);
    window.scrollTo(0, 0);
  };

  return (
    <Provider>
      <Box p={6}>
        {errorMessage && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            <Box flex="1">{errorMessage}</Box>
            <CloseButton onClick={() => setErrorMessage('')} />
          </Alert>
        )}

        <VStack align="stretch" spacing={6} maxW="800px" mx="auto">
          <AddTemplateForm
            eventId={eventId}
            onCreate={onTemplateCreate}
            onError={setError}
          />

          <Box>
            <Heading size="md" mb={3}>Existing Templates:</Heading>
            {templates.length > 0 && templates.every((t) => parseInt(t.id, 10) <= 0) && (
              <Text fontSize="sm" color="gray.500" mb={3} fontStyle="italic">
                These are default templates. Add your own custom templates above to replace them.
              </Text>
            )}
            {templates.length === 0 ? (
              <Text color="gray.500" fontStyle="italic">
                No templates yet. Add one above.
              </Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                {templates.map((template, index) => (
                  <TemplateRow
                    key={`${template.id}-${index}`}
                    template={template}
                    onUpdate={onTemplateUpdate}
                    onRemove={onTemplateRemoved}
                    onError={setError}
                  />
                ))}
              </VStack>
            )}
          </Box>
        </VStack>
      </Box>
    </Provider>
  );
};
